/*
 * What a public page's fetch came back as, so that a failed request is never
 * mistaken for "this page does not exist" or "there is nothing here".
 *
 *   ok        the API answered with a body
 *   notFound  the API answered 404: the only answer that may show the
 *             not-found page (and its noindex)
 *   failed    rate limited (429), a 5xx, any other error, or a network
 *             failure (AxiosFactory then puts a string in `data`): the page
 *             keeps its server-rendered head and offers a retry (LoadError)
 */
export const LOAD_OK = "ok";
export const LOAD_NOT_FOUND = "notFound";
export const LOAD_FAILED = "failed";

export function loadOutcome(res) {
  if (!res || typeof res !== "object") return LOAD_FAILED;
  // httpStatus is the real code; `status` folds 429 and 502-504 into 400.
  const status = res.httpStatus || res.status;
  if (status === 404) return LOAD_NOT_FOUND;
  if (status === 0 || status >= 400) return LOAD_FAILED;
  if (!res.data || typeof res.data !== "object") return LOAD_FAILED;
  return LOAD_OK;
}

export const MAX_RETRY_DELAY = 5000;
// When Retry-After is missing or unreadable (a cross-origin API does not
// expose it to scripts).
export const DEFAULT_RETRY_DELAY = 2000;

/** Milliseconds to wait before retrying a rate-limited answer, or null for any other answer. */
export function rateLimitDelay(res) {
  if (!res || (res.httpStatus !== 429 && res.code !== "RATE_LIMITED")) return null;
  const value = res.retryAfter;
  let ms = DEFAULT_RETRY_DELAY;
  if (value !== undefined && value !== null && String(value).trim() !== "") {
    // Retry-After is a number of seconds or an HTTP date.
    const seconds = Number(value);
    const at = Date.parse(value);
    if (Number.isFinite(seconds)) ms = Math.max(0, seconds * 1000);
    else if (Number.isFinite(at)) ms = Math.max(0, at - Date.now());
  }
  return Math.min(ms, MAX_RETRY_DELAY);
}

/**
 * Run `request()`; when the answer is rate limited, wait for Retry-After
 * (capped at MAX_RETRY_DELAY) and run it once more. `isLive` is checked
 * before the retry, so a page the visitor has left does not send it.
 */
export async function loadWithRetry(request, isLive = () => true) {
  const res = await request();
  const delay = rateLimitDelay(res);
  if (delay === null) return res;
  await new Promise((resolve) => setTimeout(resolve, delay));
  if (!isLive()) return res;
  return request();
}
